import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Button, Chip, Fade, InputAdornment, OutlinedInput, Paper, Stack, Typography } from '@mui/material';
import { DetailAndDelBtn, MetrcLogo, NONE, noPermTablePrefixMsg } from '../../utility';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import BaseNewAndDetail from '../../utility/outlines/BaseNewAndDetail';
import { metrcTagsClasses, roomDetailClasses, tableClasses, errFeedbackFn } from '../../utility';
import { FarmContext } from '../../../context/FarmContext';
import apiService from '../../../api/apiService';
import { de } from 'date-fns/locale';
import { FeedbackContext } from '../../../App';
import { useToken } from '../../../utility';
import { failsTokenAndSetupCheck } from '../../../checks/setup';

export default function _WasteReasonsTab(props) {
  const { farmContextValue } = useContext(FarmContext);

  const {
    currDarkThemeClass = '',
    openStrainsPopup = false,
    categoryPerms = null,
    handleSetValues = () => {},
    handleErrorWithFeedback = () => {},
    handleEntityEntryCreateRes = () => {},
    handleEntityEntryModifyRes = () => {},
    ...otherProps
  } = props;
  const [open, setOpen] = useState(false);
  const [detailChildren, setDetailChildren] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const feedbackCX = useContext(FeedbackContext);
  const { token, tokenDetails } = useToken();
  const invalidTokenState = useMemo(() => failsTokenAndSetupCheck(token, tokenDetails), [token]);

  const fetchFarmData = async (farm = selectedFarm, currentPage = page, limit = rowsPerPage, search = searchTerm) => {
    setLoading(true);
    try {
      let url = `waste-reason/?limit=${rowsPerPage}&page=${page + 1}`;
      if (selectedFilter !== 'All') {
        url += `&farm_id=${selectedFarm.farm_id}`;
      }
      if (searchTerm) {
        url += `&name__icontains=${encodeURIComponent(searchTerm)}`;
      }
      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const formattedData = formatData(response.data.results || []);
        setData(formattedData);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setLoading(false);
    }
  };

  // Function that syncs data to Metrc and then fetches the latest farm data
  const handleSyncToMetrc = async () => {
    if (selectedFarm) {
      setSyncLoading(true);
      let syncTometrcUrl = `waste-reason/?licenseNumber=${selectedFarm.license_number}&sync_to_metrc=true`;

      try {
        const response = await apiService.fetchInstance(syncTometrcUrl);

        // If the sync to Metrc is successful, fetch the latest farm data
        if (response.status === 200) {
          // Call fetchFarmData to reload the data
          fetchFarmData();

          // Provide feedback to the user
          feedbackCX.setContextValue(true, {
            type: 'success',
            message: 'Waste Reason Synced From Metrc Successfully.',
          });
          setTimeout(() => {
            feedbackCX.setContextValue(false, {});
          }, 5000);
        }
      } catch (error) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: `Error syncing to Metrc`,
        });
      } finally {
        setSyncLoading(false);
      }
    }
  };

  useEffect(() => {
    if (farmContextValue && Array.isArray(farmContextValue.farms) && farmContextValue.farms.length > 0) {
      setFarms(farmContextValue.farms);

      const defaultFarm = farmContextValue.farms[0];
      setSelectedFarm(defaultFarm);
      setSelectedFilter(defaultFarm.farm_id);
    }
  }, [farmContextValue]);

  useEffect(() => {
    if (invalidTokenState) return;

    if (selectedFarm) {
      fetchFarmData(selectedFarm, page, rowsPerPage, searchTerm, invalidTokenState);
    }
  }, [selectedFarm, page, rowsPerPage, searchTerm, invalidTokenState]);

  const formatData = formattedData => {
    return formattedData.map((item, idx) => [
      // `${idx + 1}`.padStart(2, 0),
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      ...categoryKeyTitleMap.map(e => {
        const val = item[e.key];
        if (typeof val === 'boolean') return val ? 'True' : 'False';
        return val ?? 'N/A';
      }),
      <DetailAndDelBtn {...gDItemEntryActionBtnProps(item)} />,
    ]);
  };
  const handleEntityEntryModify = (entity, prop) => async value => {
    const errConfig = {
      modify: true,
      resource: 'Strain',
    };
    let res = { success: false };
    try {
      res = await apiService.modifyInstance(entity, value).then(handleEntityEntryModifyRes(prop));
      fetchFarmData();
    } catch (err) {
      handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
    }
    return res;
  };
  const handleSetSelItem = (prop, value) => {
    setDetailChildren(state => ({
      ...state,
      selStrain: {
        ...state.selStrain,
        [prop]: value,
      },
    }));
  };

  const gDItemEntryActionBtnProps = category => ({
    currDarkThemeClass,
    toShowDelBtn: false,
    onDetailClick: () => handleItemEntryView(category),
    toShowDetailBtn: false,
  });

  const handleItemEntryView = category => {
    handleSetValues(updateKey, true);
    setOpen(true);

    setDetailChildren({
      selStrain: category,
      title: category.category_name,
      toShowSave: categoryPerms?.update ?? false,
      handleCategorySave: handleEntityEntryModify(`category/${category.id}/`, 'category'),
      handleSetSelItem,
    });
  };

  // Define your title map for the table headers
  const categoryKeyTitleMap = [
    // { key: 'category_name', title: 'Category Name' },
    // { key: 'category_type', title: 'Category Type' },
    // { key: 'quantity_type', title: 'Quantity Type' },
    { key: 'name', title: 'Name' },
    { key: 'requires_note', title: 'Requires Note' },
    { key: 'requires_waste_weight', title: 'Requires Waste Weight' },
    { key: 'requires_immature_waste_weight', title: 'Requires Immature Waste Weight' },
    { key: 'requires_mature_waste_weight', title: 'Requires Mature Waste Weight' },
    // { key: 'farm', title: 'Farm ID' }
  ];
  const updateKey = 'update';
  const tableTitles = categoryKeyTitleMap.map(e => ({ title: e.title }));

  // Functions for pagination (replace with actual logic)
  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
    // Update page and rowsPerPage (replace with state management)
  };
  const displayProps = {
    currDarkThemeClass,
    tableEntries: data,
    tableTitles,
    totalCount,
    onPageChange: handlePageChange,
    toShowAddBtn: false,
    loading,
    rowsPerPage,
    page,
    entity: 'Items',
    ...otherProps,
  };

  // Example values for the table props
  const itemTableValues = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Items Found',
    entity: 'Items',
    tableEntries: data,
    tableTitles: [{ title: 'Sl. No' }, ...categoryKeyTitleMap.map(e => ({ title: e.title })), { title: '' }],
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading: false, // Set loading to true if data is being fetched
    onClick: () => console.log('Add New Item Clicked'), // Define the add button functionality
    ...otherProps, // Spread any additional props passed from parent
  };

  const handleFilterChange = farmId => {
    const farmObj = farms.find(f => f.farm_id === farmId);
    setSelectedFarm(farmObj);
    setSelectedFilter(farmId);
    setPage(0);
  };

  const sChipsProps = farm => ({
    key: farm.farm_id,
    label: farm.farm_name,
    variant: selectedFilter === farm.farm_id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.farm_id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });
  const metrcTagFarmChipsList = (
    <Stack direction="row" spacing={1}>
      {farms.length > 0 ? farms.map(farm => <Chip key={farm.farm_id} {...sChipsProps(farm)} />) : <Typography>No farms available</Typography>}
    </Stack>
  );
  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };
  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Item Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>
              Show by Farm:
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...itemTableValues} />
        </Box>
      </Box>
    </Fade>
  );
}
